import { autoinject } from "aurelia-dependency-injection";
import { UserClaimsService } from "../../services/authentication/user-claims.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { QueryFilter, DataType, DataGridColumn, ItemSource, DataGridColumnOverride } from "../queries/queries.models";
import { Router } from "aurelia-router";
import * as Enumerable from 'linq';
import { UnitOfWorkItem } from "../../utilities/unit-of-work-observer";
import { RouteConfig } from 'aurelia-router';
import { HtmlHelper } from "../../helpers/html-helper";
import { RightShipQiRouterConstants } from "../../configuration/rightshipqi-router.constants";
import { Claims } from "../../constants/authentication/claims";
import { Reflection } from "../../utilities/reflection";
import { CompanyODataModel } from "../companies/company.models";
import { I18N } from "aurelia-i18n";
import { NumberFormatHelper } from "../../helpers/number-format-helper";
import { VesselHelper } from "../vessels/vessel.models";
import { GenesisRouterConstants } from "../../configuration/router.constants";
import { Guid } from "../../utilities/guid";
import { EntityTypes, IEntityTypeIdentifier } from "@rightship-tech/common-ui/models/basic.models";

const i18n = new I18N();

export interface IAssignEntity extends IEntityTypeIdentifier {
    assignedTo?: number;
    assignedDate?: Date,
    assignmentComment?: string;
    assignedToUserName?: string;
    id?: number;
    status?: string;
}

export interface BaseEntity {
    id: number;
}

export interface PropertyMetaModel {
    displayName: string;
    propertyName: string;
    camelCasePropertyName: string;
    type: DataType;
    parameter: string;
    secondParameter: string;
    isCollection: boolean;
    isEditableByUser: boolean;
}

export interface TableColumnMetaColumn {
    columnName: string;
    columnDisplayName: string;
    id: number;
    maxLength: number;
}

export interface EntityValidationResults {
    result: boolean;
    message: string;
}

export interface EntityTypeRelation {
    entityTypeRelationId: number;
    parentEntityType: number;
    childEntityType: number;
    displayName: string;
    relationName: string;
}

export interface BaseResponse {
    id: number;
}

export interface DeleteResponse {
    message: string;
    isSuccess: boolean;
}

export interface BaseUnitOfWorkChangeRequest {
    id: number;
    changes: { [index: string]: UnitOfWorkItem };
    identifier?: string;
}

export interface EntityTypeModel {
    entityTypeId: number;
    name: string;
    guid: string;
    isActive: boolean;
    isDefault: boolean;
    sequenceNumber: number;
}

export interface DynamicColumnModel {
    columnId: number;
    columnName: string;
    displayName: string;
    tableColumnDataTypeId: number;
    entityTypeId: number;
    parameter: string;
    secondParameter: string;
    dynamicColumnValidations: DynamicColumnValidationModel[];
    manageEntityGroupId: number;
    sequenceNumber: number;
    isActive: boolean;
}

export interface DynamicEntityType {
    entityType: EntityTypeModel;
    isOnboardingEntity: boolean;
}

export interface DynamicColumnValidationModel {
    dynamicColumnValidationId: number;
    dynamicColumnId: number;
    validationType: number;
    parameter: string;
    errorMessage: string;
}

export interface ChartDrillDownRule {
    name: string;
    queryModelUri: string;
    queryFilters?: QueryFilter[];
    propertyToFilterDrillDownOn: string;
    otherPropertyToFilterToClone: { from: string, to: string }[]
    columnsToShow: string[];
}

export interface EntityDynamicValue {
    entityId: number;
    columnId: number;
    value: string;
    castedValue: any;
    dynamicColumn: DynamicColumnModel;
}

export interface IBaseEntity {
    identifier: string;
}

export interface IEditableEntity {
    isEditable: boolean;
}

/**
* Tab/step model
*/
export interface StepModel {
    /* Tab index */
    step?: any;
    /* Tab title */
    title?: string | (() => string);
    name?: string;
    /* View-model to-be bound to the view */
    vm?: any;
    children?: StepModel[];
    parent?: any;
    /* Show the tab in the navigation bar */
    show: boolean | (() => boolean);
    isHiddenFromStepper?: boolean;
    onClick?: ((x: any) => void | boolean | Promise<void>);
    getModel?: (item) => any;
    errorsCount?: number;
    class?: string;
    lazyLoad?: boolean;
    isOpen?: boolean;
    lockedFeature?: boolean;
    unlockFeature?: ()=> void;
}

export enum KeyCode {
    Enter = 13,
    ArrowUp = 38,
    ArrowDown = 40
}

export class PropertyChangeNotifier {
    public state: any = {};

    trigger() {
        if (this.state) {
            this.state = null;
        } else {
            this.state = {};
        }
    }
}

export interface EntityTypeConfigurationModel {
    title?: string;
    entityType?: EntityTypes;
    claims: string[];
    isVisible: boolean;
    typeSingular?: string;
    properties?: string;
    format?: string | ((filter: string, item: any, isAuthenticated: boolean, isFilter: boolean) => string);
    editRoute?: string | ((item: any, route: Router) => void);
    childRoute?: string;
    filters?: QueryFilter[];
    excludeInDashboard?: () => boolean;
    gridConfig?: any;
    columnOverrides?: DataGridColumnOverride[];
    navbar?: string | boolean;
    uri: string;
    fixedColumns?: DataGridColumn[] | (() => DataGridColumn[]);
    enableDownloadExcelClaims?: string[];
    addNewTextOverride?: string;
    idPropertyName?: string;
    key?: string;
    disableDefaultSortingByIdDesc?: boolean;
}

export interface ManageEntityFilter {
    displayName: string;
    internalName: string;
}

export const QueryLookupsWithContext = [{
    lookup: "Users",
    contextProperty: "id"
}];

export const QueryLookupsWithEF = [{
    lookup: "RS_Ports",
    odataUri:"Ports"
}];

export  class SearchHelper {
    static isSearchVesselIMO(vessel, filter) {

        if (isNaN(filter)) return false;
        
        let highlightedSyntax = '';

        if (vessel.internationalMaritimeOrganizationNumber) {
            highlightedSyntax = HtmlHelper.syntaxHighlight(
                filter, 
                vessel.internationalMaritimeOrganizationNumber);

            if (highlightedSyntax.indexOf('<b>') > -1) return true;
        }
        
        if (vessel.vehicleIdentificationNumber) {
            highlightedSyntax = HtmlHelper.syntaxHighlight(
                filter, 
                vessel.vehicleIdentificationNumber);

            if (highlightedSyntax.indexOf('<b>') > -1) return true;
        }
        
        if (vessel.europeanNumberOfIdentification) {
            highlightedSyntax = HtmlHelper.syntaxHighlight(
                filter, 
                vessel.europeanNumberOfIdentification);
                
            if (highlightedSyntax.indexOf('<b>') > -1) return true;
        }
           
        return false;
    }
}

export const AllEntityTypeConfigurationConstants: EntityTypeConfigurationModel[] = [
    {
        title: "RightShipQiUsers",
        uri: "RightShipQiUsers",
        key: "RightShipQiUsers",
        typeSingular: "User",
        isVisible: true,
        properties: "fullName",
        claims: [],
        columnOverrides: [],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Users",
        uri: "UserSearch",
        key: 'UserSearch',
        typeSingular: "User",
        isVisible: true,
        editRoute: GenesisRouterConstants.RouteNames.ManageUsers,
        properties: "firstName",
        format: (filter, item, isAuthenticated, isFilter) => {
            let html = "<div class='column'>";

            if (isFilter && item.email)
                html += `<span class='prop'>${item.email}</span>`;
            else {
                html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.firstName)} ${HtmlHelper.syntaxHighlight(filter, item.lastName)}</span>`;

                if (item.email)
                    html += `<span class='prop'>${HtmlHelper.syntaxHighlight(filter, item.email)}</span>`;
            }
            html += "</div>";
            return html;
        },
        claims: [],
        columnOverrides: [],
        excludeInDashboard: () => { return true; }
    }, {
        title: "Users",
        uri: "UserSearch?internal=1",
        key: 'InternalUserSearch',
        typeSingular: "User",
        isVisible: true,
        editRoute: GenesisRouterConstants.RouteNames.ManageUsers,
        properties: "firstName",
        format: (filter, item, isAuthenticated, isFilter) => {
            let html = "<div class='column'>";

            if (isFilter && item.email)
                html += `<span class='prop'>${item.email}</span>`;
            else {
                html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.firstName)} ${HtmlHelper.syntaxHighlight(filter, item.lastName)}</span>`;

                if (item.email)
                    html += `<span class='prop'>${HtmlHelper.syntaxHighlight(filter, item.email)}</span>`;
            }
            html += "</div>";
            return html;
        },
        claims: [],
        columnOverrides: [],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Vessels",
        uri: "VesselSearch",
        key: 'InformativeVesselSearch',
        typeSingular: "Vessel",
        isVisible: true,
        editRoute: RightShipQiRouterConstants.RouteNames.VesselDetails,
        properties: "name",
        format: (filter, item, isAuthenticated, isFilter) => {

            const trackingGuid = Guid.generateGuid();

            if (isFilter && !isAuthenticated) {
                let html = isAuthenticated ? "<a class='column' href='/vessel/" + item.id + "?entity-search="+trackingGuid+"'>" : "<div class='column'>";

                html += "<div class='row nowrap'>";

                html += "<svg class='icon-arrow' version='1.1' viewBox='0 0 14 14'>" +
                    "<path d='M13,0h0.2h0.1l0,0h0.1h0.1h0.1l0,0l0.2,0.2l0.1,0.1l-0.1-0.1c0.1,0.1,0.1,0.1,0.2,0.2l0,0c0,0,0,0.1,0,0.1l0,0" +
                    "v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1l0,0c0.1,0,0.2,0.1,0.2,0.2l0,0c0,0.1,0,0.1,0,0.2v9c0,0.6-0.4,1-1,1s-1-0.4-1-1l0,0V3.4L1.7,13.7" +
                    "c-0.4,0.4-0.9,0.4-1.3,0.1l-0.1-0.1c-0.4-0.4-0.4-1,0-1.4c0,0,0,0,0,0l0,0L10.6,2H4C3.5,2,3.1,1.6,3,1.1V1c0-0.6,0.4-1,1-1H13z'/>" +
                    "</svg>";

                html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.name)}</span>`;

                if (item.internationalMaritimeOrganizationNumber)
                    html += `, <span class='prop'>${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;

                html += isAuthenticated ? "</a>" : "</div>";

                return html;
            } else {
                if (item.unfitForQuickSearch){
                    if (!SearchHelper.isSearchVesselIMO(item, filter)) {
                        return "";
                    }
                }
                
                let html = isAuthenticated ? "<a class='column' href='/vessel/" + item.id + "?entity-search="+trackingGuid+"'>" : "<div class='column'>";

                html += "<div class='row nowrap'>";

                html += "<svg class='icon-arrow' version='1.1' viewBox='0 0 14 14'>" +
                    "<path d='M13,0h0.2h0.1l0,0h0.1h0.1h0.1l0,0l0.2,0.2l0.1,0.1l-0.1-0.1c0.1,0.1,0.1,0.1,0.2,0.2l0,0c0,0,0,0.1,0,0.1l0,0" +
                    "v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1l0,0c0.1,0,0.2,0.1,0.2,0.2l0,0c0,0.1,0,0.1,0,0.2v9c0,0.6-0.4,1-1,1s-1-0.4-1-1l0,0V3.4L1.7,13.7" +
                    "c-0.4,0.4-0.9,0.4-1.3,0.1l-0.1-0.1c-0.4-0.4-0.4-1,0-1.4c0,0,0,0,0,0l0,0L10.6,2H4C3.5,2,3.1,1.6,3,1.1V1c0-0.6,0.4-1,1-1H13z'/>" +
                    "</svg>";

                html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.name)}</span>`;

                if (item.previousName)
                    html += `<span class='previous-name'>(${HtmlHelper.syntaxHighlight(filter, item.previousName)})</span>`;
                html += "</div>";

                html += "<div class='prop-container row'>";

                if (item.internationalMaritimeOrganizationNumber) {
                    html += `<span class='prop'>IMO ${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;
                }
                else if (item.vehicleIdentificationNumber) {
                    html += `<span class='prop'>VIN ${HtmlHelper.syntaxHighlight(filter, item.vehicleIdentificationNumber)}</span>`;
                }
                else if (item.europeanNumberOfIdentification) {
                    html += `<span class='prop'>ENI ${HtmlHelper.syntaxHighlight(filter, item.europeanNumberOfIdentification)}</span>`;
                }
                else if (item.maritimeMobileServiceIdentityNumber) {
                    html += `<span class='prop'>MMSI ${HtmlHelper.syntaxHighlight(filter, item.maritimeMobileServiceIdentityNumber)}</span>`;
                }

                if (item.deadweightTonnage) {
                    html += `<span class='prop'>DWT ${NumberFormatHelper.getFormattedValue(item.deadweightTonnage, null, 0, 0)}</span>`;
                }
                if (item.vesselTypeValue) {
                    html += `<span class='prop'>${item.vesselTypeValue}</span>`;
                }

                html += "</div>";

                html += isAuthenticated ? "</a>" : "</div>";

                return html;
            }
        },
        claims: [],
        columnOverrides: [
            {
                getCellInlineStyles: null,
                getComponentClass: null,
                property: 'dateOfBuild',
                valueConverter: (value) => {
                    return value ? VesselHelper.getAgeFromDate(value) : null;
                }
            },
            {
                property: 'safetyRating',
                valueConverter: (value) => {
                    return value && value > 0 ? value : " ";
                }
            }
        ],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Vessels",
        uri: "VesselSearch?ds=1",
        key: 'SubmitFormVesselSearch',
        typeSingular: "Vessel",
        isVisible: true,
        editRoute: RightShipQiRouterConstants.RouteNames.VesselDetails,
        properties: "name",
        format: (filter, item, isAuthenticated, isFilter) => {
            if (isFilter && !isAuthenticated) {
                let html = isAuthenticated ? "<a class='column'>" : "<div class='column'>";

                html += "<div class='row nowrap'>";

                html += "<svg class='icon-arrow' version='1.1' viewBox='0 0 14 14'>" +
                    "<path d='M13,0h0.2h0.1l0,0h0.1h0.1h0.1l0,0l0.2,0.2l0.1,0.1l-0.1-0.1c0.1,0.1,0.1,0.1,0.2,0.2l0,0c0,0,0,0.1,0,0.1l0,0" +
                    "v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1l0,0c0.1,0,0.2,0.1,0.2,0.2l0,0c0,0.1,0,0.1,0,0.2v9c0,0.6-0.4,1-1,1s-1-0.4-1-1l0,0V3.4L1.7,13.7" +
                    "c-0.4,0.4-0.9,0.4-1.3,0.1l-0.1-0.1c-0.4-0.4-0.4-1,0-1.4c0,0,0,0,0,0l0,0L10.6,2H4C3.5,2,3.1,1.6,3,1.1V1c0-0.6,0.4-1,1-1H13z'/>" +
                    "</svg>";

                html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.name)}</span>`;

                if (item.internationalMaritimeOrganizationNumber)
                    html += `, <span class='prop'>${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;

                html += isAuthenticated ? "</a>" : "</div>";
                return html;
            } else {
                let html = "<div class='column'>";

                html += "<div class='row nowrap'>";

                html += "<svg class='icon-arrow' version='1.1' viewBox='0 0 14 14'>" +
                    "<path d='M13,0h0.2h0.1l0,0h0.1h0.1h0.1l0,0l0.2,0.2l0.1,0.1l-0.1-0.1c0.1,0.1,0.1,0.1,0.2,0.2l0,0c0,0,0,0.1,0,0.1l0,0" +
                    "v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1l0,0c0.1,0,0.2,0.1,0.2,0.2l0,0c0,0.1,0,0.1,0,0.2v9c0,0.6-0.4,1-1,1s-1-0.4-1-1l0,0V3.4L1.7,13.7" +
                    "c-0.4,0.4-0.9,0.4-1.3,0.1l-0.1-0.1c-0.4-0.4-0.4-1,0-1.4c0,0,0,0,0,0l0,0L10.6,2H4C3.5,2,3.1,1.6,3,1.1V1c0-0.6,0.4-1,1-1H13z'/>" +
                    "</svg>";

                html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.name)}</span>`;

                if (item.previousName)
                    html += `<span class='previous-name'>(${HtmlHelper.syntaxHighlight(filter, item.previousName)})</span>`;
                html += "</div>";

                html += "<div class='prop-container row'>";
                if (item.internationalMaritimeOrganizationNumber) {
                    html += `<span class='prop'>IMO ${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;
                }
                if (item.vehicleIdentificationNumber) {
                    html += `<span class='prop'>VIN ${HtmlHelper.syntaxHighlight(filter, item.vehicleIdentificationNumber)}</span>`;
                }
                if (item.europeanNumberOfIdentification) {
                    html += `<span class='prop'>ENI ${HtmlHelper.syntaxHighlight(filter, item.europeanNumberOfIdentification)}</span>`;
                }
                if (item.maritimeMobileServiceIdentityNumber) {
                    html += `<span class='prop'>MMSI ${HtmlHelper.syntaxHighlight(filter, item.maritimeMobileServiceIdentityNumber)}</span>`;
                }

                if (item.deadweightTonnage)
                    html += `<span class='prop'>DWT ${NumberFormatHelper.getFormattedValue(item.deadweightTonnage, null, 0, 0)}</span>`;
                if (item.vesselTypeValue)
                    html += `<span class='prop'>${item.vesselTypeValue}</span>`;

                html += "</div>";

                const imo = item.internationalMaritimeOrganizationNumber ? item.internationalMaritimeOrganizationNumber :
                    item.vehicleIdentificationNumber ? item.vehicleIdentificationNumber :
                        item.europeanNumberOfIdentification ? item.europeanNumberOfIdentification :
                            item.maritimeMobileServiceIdentityNumber ? item.maritimeMobileServiceIdentityNumber : "";

                html += "<div class='links'><a class='link' href='new-psc?vesselId=" + item.id + "&internationalMaritimeOrganizationNumber=" + imo + "&vesselName=" + item.name + "'>Submit PSC</a>";
                html += "<a class='link' href='new-incident?vesselId=" + item.id + "&internationalMaritimeOrganizationNumber=" + imo + "&vesselName=" + item.name + "'>Submit Incident</a></div>";

                html += "</div>";
                return html;
            }
        },
        claims: [],
        columnOverrides: [
            {
                getCellInlineStyles: null,
                getComponentClass: null,
                property: 'dateOfBuild',
                valueConverter: (value) => {
                    return value ? VesselHelper.getAgeFromDate(value) : null;
                }
            },
            {
                property: 'safetyRating',
                valueConverter: (value) => {
                    return value && value > 0 ? value : " ";
                }
            }
        ],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Vessels",
        uri: "VesselSearch",
        key: 'BasicVesselSearch',
        typeSingular: "Vessel",
        isVisible: true,
        editRoute: RightShipQiRouterConstants.RouteNames.VesselDetails,
        properties: "name",
        format: (filter, item) => {

            let html = "<div class='entity-finder-search-vessel-item column'><p class='name'>" + item.name + "</p>"

            html += "<div class='prop-container row'>";
            if (item.internationalMaritimeOrganizationNumber)
                html += `<span class='prop'>IMO ${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;

            if (item.vehicleIdentificationNumber)
                html += `<span class='prop'>VIN ${HtmlHelper.syntaxHighlight(filter, item.vehicleIdentificationNumber)}</span>`;

            if (item.europeanNumberOfIdentification)
                html += `<span class='prop'>ENI ${HtmlHelper.syntaxHighlight(filter, item.europeanNumberOfIdentification)}</span>`;

            if (item.maritimeMobileServiceIdentityNumber)
                html += `<span class='prop mmsi'>MMSI ${HtmlHelper.syntaxHighlight(filter, item.maritimeMobileServiceIdentityNumber)}</span>`;

            if (item.deadweightTonnage)
                html += `<span class='prop'>DWT ${NumberFormatHelper.getFormattedValue(item.deadweightTonnage, null, 0, 0)}</span>`;
            if (item.vesselTypeValue)
                html += `<span class='prop'>${item.vesselTypeValue}</span>`;

            html += "</div>";
            html += "</div>";

            return html;
        },
        claims: [],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Vessels",
        uri: "AllVesselSearch",
        key: 'AllVesselSearch',
        typeSingular: "Vessel",
        isVisible: true,
        editRoute: RightShipQiRouterConstants.RouteNames.VesselDetails,
        properties: "name",
        format: (filter, item) => {

            let html = "<div class='entity-finder-search-vessel-item column'><p class='name'>" + item.name + "</p>"

            html += "<div class='prop-container row'>";
            if (item.internationalMaritimeOrganizationNumber){
                html += `<span class='prop'>IMO ${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;
            }

            if (item.vehicleIdentificationNumber){
                html += `<span class='prop'>VIN ${HtmlHelper.syntaxHighlight(filter, item.vehicleIdentificationNumber)}</span>`;
            }

            if (item.europeanNumberOfIdentification){
                html += `<span class='prop'>ENI ${HtmlHelper.syntaxHighlight(filter, item.europeanNumberOfIdentification)}</span>`;
            }

            if (item.maritimeMobileServiceIdentityNumber){
                html += `<span class='prop'>MMSI ${HtmlHelper.syntaxHighlight(filter, item.maritimeMobileServiceIdentityNumber)}</span>`;
            }

            if (item.deadweightTonnage){
                html += `<span class='prop'>DWT ${NumberFormatHelper.getFormattedValue(item.deadweightTonnage, null, 0, 0)}</span>`;
            }

            if (item.vesselTypeValue)
                html += `<span class='prop'>${item.vesselTypeValue}</span>`;

            html += "</div>";
            html += "</div>";

            return html;
        },
        claims: [],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Vessels",
        uri: "Vessels",
        typeSingular: "Vessel",
        isVisible: true,
        editRoute: RightShipQiRouterConstants.RouteNames.VesselDetails,
        properties: "name",
        claims: [],
        columnOverrides: [{
            getCellInlineStyles: null,
            getComponentClass: null,
            property: 'dateOfBuild',
            valueConverter: (value) => {
                return value ? VesselHelper.getAgeFromDate(value) : null;
            }
        }],
        format: (filter, item) => {

            let html = "<div class='entity-finder-search-vessel-item column'><p class='name'>" + item.name + "</p>"

            html += "<div class='prop-container row'>";
            if (item.internationalMaritimeOrganizationNumber)
                html += `<span class='prop'>IMO ${HtmlHelper.syntaxHighlight(filter, item.internationalMaritimeOrganizationNumber)}</span>`;

            if (item.vehicleIdentificationNumber)
                html += `<span class='prop'>VIN ${HtmlHelper.syntaxHighlight(filter, item.vehicleIdentificationNumber)}</span>`;

            if (item.europeanNumberOfIdentification)
                html += `<span class='prop'>ENI ${HtmlHelper.syntaxHighlight(filter, item.europeanNumberOfIdentification)}</span>`;

            if (item.maritimeMobileServiceIdentityNumber)
                html += `<span class='prop mmsi'>MMSI ${HtmlHelper.syntaxHighlight(filter, item.maritimeMobileServiceIdentityNumber)}</span>`;

            if (item.dwt)
                html += `<span class='prop'>DWT ${NumberFormatHelper.getFormattedValue(item.dwt, null, 0, 0)}</span>`;
            if (item.shipType)
                html += `<span class='prop'>${item.shipType}</span>`;

            html += "</div>";
            html += "</div>";

            return html;
        },
    },
    {
        title: "Companies",
        uri: "CompanySearch",
        key: 'InformativeCompanySearch',
        entityType: EntityTypes.Company,
        typeSingular: "Company",
        editRoute: RightShipQiRouterConstants.RouteNames.Company,
        isVisible: true,
        properties: "fullName,owCode",
        format: (filter, item) => {

            const trackingGuid = Guid.generateGuid();
            let html = "<a class='column' href='/company/" + item.id + "?entity-search="+trackingGuid+"'><div class='row nowrap'>";
            html += "<svg class='icon-arrow' version='1.1' viewBox='0 0 14 14'>" +
                "<path d='M13,0h0.2h0.1l0,0h0.1h0.1h0.1l0,0l0.2,0.2l0.1,0.1l-0.1-0.1c0.1,0.1,0.1,0.1,0.2,0.2l0,0c0,0,0,0.1,0,0.1l0,0" +
                "v0.1c0,0,0,0,0,0.1c0,0,0,0,0,0.1l0,0c0.1,0,0.2,0.1,0.2,0.2l0,0c0,0.1,0,0.1,0,0.2v9c0,0.6-0.4,1-1,1s-1-0.4-1-1l0,0V3.4L1.7,13.7" +
                "c-0.4,0.4-0.9,0.4-1.3,0.1l-0.1-0.1c-0.4-0.4-0.4-1,0-1.4c0,0,0,0,0,0l0,0L10.6,2H4C3.5,2,3.1,1.6,3,1.1V1c0-0.6,0.4-1,1-1H13z'/>" +
                "</svg>";
            html += `<span class='name'>${HtmlHelper.syntaxHighlight(filter, item.shortCompanyName)}</span></div>`;
            html += "<div class='prop-container row'>"
            if (item.countryName)
                html += `<span class='prop'>${item.countryName}</span>`;
            html += `<span class='prop'>${item.vesselsUnderManagementWithAllRoles ?? 0} vessels In Service`;

            if (item.vesselsUnderManagement && item.vesselsUnderManagement > 0) {
                html += ` (${item.vesselsUnderManagement} as DOC)</span>`;
            } else {
                html += `</span>`;
            }

            html += "</div></a>";
            return html;
        },
        claims: [],
        columnOverrides: [
            {
                property: "country",
                titleToolTip: i18n.tr('company-country-tooltip-text')
            },
            {
                property: "registrationCountry",
                titleToolTip: i18n.tr('company-nationality-of-registration-tooltip-text')
            },
            {
                property: "controlCountry",
                titleToolTip: i18n.tr('company-control-country-tooltip-text')
            },
            {
                property: "docCount",
                titleToolTip: i18n.tr('company-vessels-under-management-tooltip-text')
            }
        ],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Companies",
        uri: "CompanySearch",
        key: 'BasicCompanySearch',
        entityType: EntityTypes.Company,
        typeSingular: "Company",
        editRoute: RightShipQiRouterConstants.RouteNames.Company,
        isVisible: true,
        properties: "fullName",
        format: (filter, item) => {

            return item.shortCompanyName;
        },
        claims: [],
        columnOverrides: [
            {
                property: "country",
                titleToolTip: i18n.tr('company-country-tooltip-text')
            },
            {
                property: "registrationCountry",
                titleToolTip: i18n.tr('company-nationality-of-registration-tooltip-text')
            },
            {
                property: "controlCountry",
                titleToolTip: i18n.tr('company-control-country-tooltip-text')
            },
            {
                property: "docCount",
                titleToolTip: i18n.tr('company-vessels-under-management-tooltip-text')
            }
        ],
        excludeInDashboard: () => { return true; }
    },
    {
        title: "Companies",
        uri: "Companies",
        entityType: EntityTypes.Company,
        typeSingular: "Company",
        editRoute: RightShipQiRouterConstants.RouteNames.Company,
        isVisible: true,
        properties: "fullName",
        claims: [],
        columnOverrides: [
            {
                property: Reflection.getPropertyName<CompanyODataModel>(x => x.docCount),
                titleToolTip: i18n.tr('company-vessels-under-management-tooltip-text')
            },
            {
                property: Reflection.getPropertyName<CompanyODataModel>(x => x.country),
                titleToolTip: i18n.tr('company-country-tooltip-text'),
                getCellClass: () => {
                    return "vertical";
                }
            },
            {
                property: Reflection.getPropertyName<CompanyODataModel>(x => x.registrationCountry),
                titleToolTip: i18n.tr('company-nationality-of-registration-tooltip-text'),
                getCellClass: () => {
                    return "vertical";
                }
            },
            {
                property: Reflection.getPropertyName<CompanyODataModel>(x => x.controlCountry),
                titleToolTip: i18n.tr('company-control-country-tooltip-text'),
                getCellClass: () => {
                    return "vertical";
                }
            }
        ]
    }, {
        title: "Customers",
        uri: "Customers",
        entityType: EntityTypes.Customer,
        typeSingular: "Customer",
        editRoute: "",
        isVisible: true,
        format: "{0}",
        properties: "name",
        claims: [Claims.CanAccessCustomers]
    }, {
        title: "Business Units",
        uri: "BusinessUnits",
        entityType: EntityTypes.Customer,
        typeSingular: "BusinessUnit",
        editRoute: "",
        format: "{0}",
        properties: "name",
        isVisible: true,
        claims: [Claims.CanAccessCustomers]
    }, {
        title: "Lists",
        uri: "Lists",
        entityType: EntityTypes.List,
        typeSingular: "List",
        editRoute: "",
        isVisible: true,
        claims: [Claims.CanAccessLists]
    },
    // removed as per PX-6404
    // {
    //     title: "MyFleet",
    //     uri: "MyFleet",
    //     typeSingular: "MyFleet",
    //     isVisible: true,
    //     editRoute: RightShipQiRouterConstants.RouteNames.MyFleetVessel,
    //     properties: "name",
    //     claims: [],
    //     columnOverrides: [{
    //         getCellInlineStyles: null,
    //         getComponentClass: null,
    //         property: 'dateOfBuild',
    //         valueConverter: (value, item) => {
    //             return value ? VesselHelper.getAgeFromDate(value): null;
    //         }
    //     }]
    // },
    {
        title: "Client Forms",
        uri: "Onboardings",
        typeSingular: "Client Form",
        isVisible: true,
        claims: []
    },
    {
        title: "Ports",
        uri: "Ports",
        typeSingular: "Port",
        format: "{0}, {1}",
        properties: "name,countryName",
        isVisible: true,
        claims: [],
        idPropertyName: "portId"
    },
    {
        title: "Incidents",
        uri: "Incidents",
        typeSingular: "Incident",
        format: "INC-{0}",
        properties: "incidentId",
        isVisible: true,
        claims: [],
        idPropertyName: "incidentId"
    },
    {
        title: "Psc Inspections",
        uri: "Pscs",
        typeSingular: "PscInspection",
        format: "PSC-{0}",
        properties: "pscInspectionId",
        isVisible: true,
        claims: [],
        idPropertyName: "pscInspectionId"
    },
    {
        title: "Vets",
        uri: "Vets",
        typeSingular: "Vet",
        isVisible: true,
        claims: []
    },
    {
        title: "Vessels Report",
        uri: "VesselReport",
        typeSingular: "Vessel Report",
        isVisible: true,
        claims: []
    },
    {
        title: "PSCs Report",
        uri: "PscReport",
        typeSingular: "PSC Report",
        isVisible: true,
        claims: []
    },
    {
        title: "GHG Custom Report",
        uri: "GhgCustomReports",
        typeSingular: "GHG Custom Report",
        isVisible: true,
        claims: []
    },    
    {
        title: "Ports",
        uri: "Ports",
        typeSingular: "Port",
        isVisible: true,
        claims: [],
        format: "{0}",
        properties: "name",
    }, 
    {
        title: "Billing Customer",
        uri: "BillingCustomers",
        typeSingular: "Billing Customer",
        isVisible: true,
        claims: [],
        format: "{0}",
        properties: "name",
    },
    {
        title: "Billing Account",
        uri: "BillingAccounts",
        typeSingular: "Billing Account",
        isVisible: true,
        claims: [],
        format: "{0}",
        properties: "name",
    },
    {
        title: "Countries",
        uri: "Countries",
        typeSingular: "Country",
        isVisible: true,
        claims: [],
        format: "{0}",
        properties: "name"
    },
    {
        title: "Inspectors",
        uri: "Inspectors",
        typeSingular: "Inspector",
        isVisible: true,
        claims: [],
        format: "{0}, {1}",
        properties: "inspectorName,inspectionCompany"
    },
    {
        title: "Rule Collections",
        uri: "RuleCollections",
        typeSingular: "Rule Collection",
        isVisible: true,
        claims: [],
        format: "{0}",
        properties: "name"
    },
    {
        title: "DOCs",
        uri: "CompanySearch",
        key: 'AllCompanySearch',
        entityType: EntityTypes.Company,
        typeSingular: "Company",
        editRoute: "company",
        isVisible: true,
        properties: "fullName,owCode",
        format: (filter, item) => {

            let html = `<div class='name'>${HtmlHelper.syntaxHighlight(filter, item.shortCompanyName)}</div>`;
            html += "<div class='prop-container row'>"
            if (item.countryName)
                html += `<span class='prop'>${item.countryName}</span>`;
            html += `<span class='prop'>${item.vesselsUnderManagementWithAllRoles ?? 0} vessels In Service`;

            if (item.vesselsUnderManagement && item.vesselsUnderManagement > 0) {
                html += ` (${item.vesselsUnderManagement} as DOC)</span>`;
            } else {
                html += `</span>`;
            }

            html += "</div></a>";
            return html;
        },
        claims: [],
        columnOverrides: [
            {
                property: "country",
                titleToolTip: "Country of domicile, physical location of the company."
            },
            {
                property: "registrationCountry",
                titleToolTip: "Country where the company is legally registered as an entity."
            },
            {
                property: "controlCountry",
                titleToolTip: "Actual nationality of company regardless of physical location."
            },
            {
                property: "docCount",
                titleToolTip: "Vessels that this company holds a DOC role."
            }
        ]
    },
];

@autoinject
export class EntityTypeConfigurationHelper {
    constructor(private userClaimsService: UserClaimsService) { }

    public getConfigurationWithoutPermissions(entityUriList: string[], configuration: EntityTypeConfigurationModel[] = null): EntityTypeConfigurationModel[] {
        return Enumerable.from(configuration && configuration.length > 0 ? configuration : AllEntityTypeConfigurationConstants)
            .where(x => entityUriList.length === 0 || Enumerable.from(entityUriList).contains(x.uri))
            .toArray();
    }

    public getConfiguration(entityUriList: string[], configuration: EntityTypeConfigurationModel[] = null): EntityTypeConfigurationModel[] {
        return Enumerable.from(configuration && configuration.length > 0 ? configuration : AllEntityTypeConfigurationConstants)
            .where(x => entityUriList.length === 0 || Enumerable.from(entityUriList).contains(x.uri))
            .where(x => entityUriList.length === 0 || this.userClaimsService.hasPermissions(x.claims))
            .toArray();
    }

    public getConfigurationByKey(keys: string[], configuration: EntityTypeConfigurationModel[] = null): EntityTypeConfigurationModel[] {
        return Enumerable.from(configuration && configuration.length > 0 ? configuration : AllEntityTypeConfigurationConstants)
            .where(x => x.key && x.key.length === 0 || Enumerable.from(keys).contains(x.key))
            .where(x => x.key && x.key.length === 0 || this.userClaimsService.hasPermissions(x.claims))
            .toArray();
    }

    public getConfigurationForDashboard(entityUriList: string[]): EntityTypeConfigurationModel[] {
        return Enumerable.from(AllEntityTypeConfigurationConstants)
            .where(x => entityUriList.length === 0 || Enumerable.from(entityUriList).contains(x.uri))
            .where(x => !x.excludeInDashboard || !x.excludeInDashboard())
            .where(x => entityUriList.length === 0 || this.userClaimsService.hasPermissions(x.claims))
            .toArray();
    }
}

export abstract class BaseModal {

    constructor(protected dialogClient: DialogClientService) { }

    public preventFromHidingModal(e) {
        e.stopPropagation();
    }

    protected getKey(): string {
        return 'default';
    }

    public hideModal(callback: () => void = null) {
        this.dialogClient.hide(true, this.getKey());
        if (callback) {
            callback();
        }
    }
}

export interface MenuTab {
    key: string;
    title?: string | (() => string);
    icon?: string | (() => string) | SvgIcon;
    routes: ExtendedRouteConfig[];
    isVisible: boolean;
    showDropdownMenu?: boolean; //for touch device clicks
    lockedFeature?: boolean;
}

export interface HelpCenterNav {
    key: string;
    title?: string | (() => string);
    routes: HelpCenterRouteConfig[];
}

export interface InspectionClientNav {
    key: string;
    title?: string | (() => string);
    routes: InspectionClientRouteConfig[];
}

export interface InspectionClientRouteConfig extends RouteConfig {
    queryString?: string;
    title: string;
    isSelected?: boolean;
}

export interface HelpCenterRouteConfig extends RouteConfig {
    queryString?: string;
    title: string;
    isSelected?: boolean;
}

export interface SvgIcon {
    class: string;
    d?: string;
    viewBox?: string;
    width?: string;
    height?: string;
}

export interface ListRouteConfig extends ExtendedRouteConfig {
    editRoute?: ExtendedRouteConfig | ((x: any) => string);
    getParams?: (item) => any;
    createNewRoute?: ExtendedRouteConfig;
    hasNavbar?: boolean;
    idKey?: string;
}

export interface ExtendedRouteConfig extends RouteConfig {
    queryString?: string;
    entity?: string;
    class?: string;
    iconType?: number;
    icon?: string | (() => string) | SvgIcon;
    claimsRequired?: string[] | (() => string[]);
    entityTypeRoute?: string;
    children?: ExtendedRouteConfig[];
    isVisible?: boolean;
    pageCount?: number;
    onItemSourcePrepared?: (itemSource: ItemSource) => void;
    // Explicit uri for the custom header component in queries.html. The default is uri.
    headerUri?: string;
    // Explicit uri for the custom nav bar component in queries.html. The default is uri.
    navbarUri?: string;
    // Specifies that you want the header component to be rendered.
    hasHeader?: boolean;
    // Override the default text that is in the add new button in queries-editor.html
    addNewTextOverride?: string;
    // default selection mode for the queries data-grid. Default is single.
    selectionMode?: (() => string) | string;
    claimsRequireType?: ClaimsRequireTypeEnum;
}

export enum ClaimsRequireTypeEnum {
    RequireAll = 1,
    RequireAny = 2
}

export interface SelectedItemsRequest {
    ids: number[],
    allSelected: boolean;
    filters: QueryFilter[];
}

export enum EmailStatusesEnum {
    PendingApproval = 1,
    SucceededApproved = 2,
    Rejected = 3,
    Failed = 4,
    SucceededAuto = 5,
    Blocked = 6
}
